import graphqlClient from './graphql-client';
import { getApiKey } from './keyMappings';

// GraphQL query for fetching translations by locale
const TRANSLATION_BY_LOCALE_QUERY = `
  query TranslationByLocale($locale: String!) {
    translationByLocale(locale: $locale) {
      id
      languageCode
      translatedValue
      translationKey
    }
  }
`;

class TranslationService {
  constructor() {
    this.translations = {};
    this.loadingPromises = {};
    this.fallbackLocale = 'DE'; // German as fallback
  }

  /**
   * Load translations for a specific locale from GraphQL API
   * @param {string} locale - The locale to load (e.g., 'EN', 'DE', 'FR')
   * @returns {Promise<void>}
   */
  async loadTranslations(locale) {
    // If translations are already loaded or being loaded, return
    if (this.translations[locale] || this.loadingPromises[locale]) {
      return this.loadingPromises[locale] || Promise.resolve();
    }

    // Check localStorage cache first
    const cacheKey = `translations_${locale}`;
    const cachedTranslations = localStorage.getItem(cacheKey);
    
    if (cachedTranslations) {
      try {
        this.translations[locale] = JSON.parse(cachedTranslations);
        return Promise.resolve();
      } catch (error) {
        console.warn('Failed to parse cached translations:', error);
        localStorage.removeItem(cacheKey);
      }
    }

    // Create loading promise
    this.loadingPromises[locale] = this.fetchTranslationsFromAPI(locale);
    
    try {
      await this.loadingPromises[locale];
    } finally {
      delete this.loadingPromises[locale];
    }
  }

  /**
   * Fetch translations from GraphQL API
   * @param {string} locale 
   * @returns {Promise<void>}
   */
  async fetchTranslationsFromAPI(locale) {
    try {
      // Convert locale to lowercase for API call (your API expects lowercase)
      const apiLocale = locale.toLowerCase();
      
      const result = await graphqlClient.query(TRANSLATION_BY_LOCALE_QUERY, { locale: apiLocale });

      if (result.data && result.data.translationByLocale) {
        // Convert array to key-value object for faster lookups
        const translationsObject = {};
        result.data.translationByLocale.forEach(translation => {
          translationsObject[translation.translationKey] = translation.translatedValue;
        });

        this.translations[locale] = translationsObject;

        // Cache in localStorage with uppercase locale key
        const cacheKey = `translations_${locale}`;
        localStorage.setItem(cacheKey, JSON.stringify(translationsObject));
        
        console.log(`✅ Loaded ${result.data.translationByLocale.length} translations for ${locale}`, translationsObject);
      } else {
        console.warn(`No translations found for locale: ${locale}`);
        this.translations[locale] = {};
      }
    } catch (error) {
      console.error(`Failed to load translations for locale ${locale}:`, error);
      // Initialize empty object to prevent repeated failed requests
      this.translations[locale] = {};
      throw error;
    }
  }

  /**
   * Get translation for a specific key and locale
   * @param {string} key - Translation key (e.g., 'home.hero.title')
   * @param {string} locale - Locale code (e.g., 'EN', 'DE')
   * @param {string} fallback - Fallback text if translation not found
   * @returns {string} Translated text or fallback
   */
  getTranslation(key, locale, fallback = key) {
    // Check if translations are loaded for the locale
    if (!this.translations[locale]) {
      return fallback;
    }

    // Try to get translation with the hierarchical key first
    let translation = this.translations[locale][key];
    
    // If not found, try with mapped API key
    if (!translation) {
      const apiKey = getApiKey(key);
      translation = this.translations[locale][apiKey];
    }
    
    if (translation) {
      return translation;
    }

    // Try fallback locale if available
    if (locale !== this.fallbackLocale && this.translations[this.fallbackLocale]) {
      const fallbackTranslation = this.translations[this.fallbackLocale][key] || 
                                   this.translations[this.fallbackLocale][getApiKey(key)];
      if (fallbackTranslation) {
        return fallbackTranslation;
      }
    }

    return fallback;
  }

  /**
   * Clear translation cache for a locale
   * @param {string} locale 
   */
  clearCache(locale) {
    delete this.translations[locale];
    const cacheKey = `translations_${locale}`;
    localStorage.removeItem(cacheKey);
  }

  /**
   * Clear all translation caches
   */
  clearAllCaches() {
    this.translations = {};
    // Clear all translation caches from localStorage
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('translations_')) {
        localStorage.removeItem(key);
      }
    });
  }

  /**
   * Check if translations are loaded for a locale
   * @param {string} locale 
   * @returns {boolean}
   */
  isLoaded(locale) {
    return !!this.translations[locale];
  }
}

// Create and export singleton instance
const translationService = new TranslationService();
export default translationService;
